import React from "react";
import { Archive, RefreshCw, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ProjectStatusControlProps {
  status: "active" | "inactive" | "archived";
  onChangeStatus: (newStatus: "active" | "inactive" | "archived") => void;
  isOwner: boolean;
}

const ProjectStatusControl: React.FC<ProjectStatusControlProps> = ({
  status,
  onChangeStatus,
  isOwner,
}) => {
  if (!isOwner) {
    return (
      <Badge
        variant="outline"
        className={cn(
          "px-2 py-1",
          status === "active"
            ? "bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
            : status === "inactive"
            ? "bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600"
            : "bg-amber-50 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700"
        )}
      >
        {status === "active" && (
          <>
            <Play className="w-3 h-3 mr-1" />
            Active
          </>
        )}
        {status === "inactive" && (
          <>
            <Pause className="w-3 h-3 mr-1" />
            Inactive
          </>
        )}
        {status === "archived" && (
          <>
            <Archive className="w-3 h-3 mr-1" />
            Archived
          </>
        )}
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Badge
        variant="outline"
        className={cn(
          "px-2 py-1",
          status === "active"
            ? "bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
            : status === "inactive"
            ? "bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600"
            : "bg-amber-50 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700"
        )}
      >
        {status === "active" && (
          <>
            <Play className="w-3 h-3 mr-1" />
            Active
          </>
        )}
        {status === "inactive" && (
          <>
            <Pause className="w-3 h-3 mr-1" />
            Inactive
          </>
        )}
        {status === "archived" && (
          <>
            <Archive className="w-3 h-3 mr-1" />
            Archived
          </>
        )}
      </Badge>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Change Status
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {status !== "active" && (
            <DropdownMenuItem onClick={() => onChangeStatus("active")}>
              <Play className="w-3 h-3 mr-2 text-green-600" />
              <span>Set as Active</span>
            </DropdownMenuItem>
          )}
          {status !== "inactive" && (
            <DropdownMenuItem onClick={() => onChangeStatus("inactive")}>
              <Pause className="w-3 h-3 mr-2 text-gray-600 dark:text-gray-400" />
              <span>Set as Inactive</span>
            </DropdownMenuItem>
          )}
          {status !== "archived" && (
            <DropdownMenuItem onClick={() => onChangeStatus("archived")}>
              <Archive className="w-3 h-3 mr-2 text-amber-600" />
              <span>Archive</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProjectStatusControl;
